import Flex from 'antd/lib/flex'
import Select from 'antd/lib/select'
import Popover from 'antd/lib/popover'
import Button from 'antd/lib/button'
import EyeFilled from '@ant-design/icons/lib/icons/EyeFilled'
import styles from '../TaskNavigator/TaskNavigator.module.less'
import classes from './JobPreviewNavigator.module.less'
import { useCallback, useMemo } from 'react'
import { useDialog } from 'Hooks/use-dialog'
import { StepType, StepTypes } from 'Types/Tasks.types'
import { useLocation, useParams, useNavigate, useSearchParams } from 'react-router-dom'

export type JobPreviewNavigatorProps = {
    jobId: string
    taskData: {
        stepName: string
        stepType: StepTypes
        taskId: string
    }[]
    childJobs: { id: string }[]
    steps: StepType[]
    isRobotics?: boolean
}

type NavigationOption = {
    key: string
    label: string
    value: string
    isDefault: boolean
    stepId?: string
    taskId?: string
}

const DROPDOWN_STYLE = {
    padding: '0px',
    borderRadius: '4px',
    border: '1px solid #F7F7F8',
}

export const JobPreviewNavigator = (props: JobPreviewNavigatorProps) => {
    const isRobotics = !!props.isRobotics
    const { jobId: paramJobId } = useParams()
    const navigate = useNavigate()
    const location = useLocation()
    const [searchParams, setSearchParams] = useSearchParams()
    const cardDialog = useDialog(false)
    const {
        jobId: locationJobId,
        stepId: locationStepId,
        taskId: locationTaskId,
    } = location.state ?? {}
    const childJobIdParam = searchParams.get('childJobId')
    const currentJobId = isRobotics ? childJobIdParam || props.jobId : locationJobId || props.jobId

    const getOptionRenderComponent = useCallback((option: { data: NavigationOption }) => {
        return (
            <Flex justify="space-between" gap={20}>
                <div className={styles['current-task-view-title']}>{option.data.label}</div>
            </Flex>
        )
    }, [])

    const stepNameToId = useMemo(() => {
        return props.steps.reduce(
            (acc, step) => {
                acc[step.name] = step.id
                return acc
            },
            {} as Record<string, string>
        )
    }, [props.steps])

    const options = useMemo<NavigationOption[]>(() => {
        const navigationOptions: NavigationOption[] = []
        const seenSteps = new Set<string>()
        const stepAttemptCount = new Map<string, number>()

        props.taskData.forEach(task => {
            const stepKey = task.stepName

            if (!seenSteps.has(stepKey)) {
                seenSteps.add(stepKey)
                const stepId = stepNameToId[task.stepName]

                if (stepId) {
                    navigationOptions.push({
                        key: `default-${stepId}`,
                        label: `${task.stepName} (Default)`,
                        value: `default-${stepId}`,
                        isDefault: true,
                        stepId: stepId,
                    })
                }
            }

            const currentAttempt = (stepAttemptCount.get(stepKey) || 0) + 1
            stepAttemptCount.set(stepKey, currentAttempt)

            navigationOptions.push({
                key: task.taskId,
                label: `${task.stepName} - Attempt ${currentAttempt}`,
                value: task.taskId,
                isDefault: false,
                taskId: task.taskId,
            })
        })

        props.steps.forEach(step => {
            if (!seenSteps.has(step.name)) {
                seenSteps.add(step.name)
                navigationOptions.push({
                    key: `default-${step.id}`,
                    label: `${step.name} (Default)`,
                    value: `default-${step.id}`,
                    isDefault: true,
                    stepId: step.id,
                })
            }
        })

        return navigationOptions
    }, [props.taskData, props.steps, stepNameToId])

    const currentValue = useMemo(() => {
        if (locationTaskId) {
            return locationTaskId
        }
        if (locationStepId) {
            return `default-${locationStepId}`
        }
        if (props.taskData.length > 0) {
            return props.taskData[props.taskData.length - 1].taskId
        }
        if (props.steps.length > 0) {
            return `default-${props.steps[0].id}`
        }
        return undefined
    }, [locationTaskId, locationStepId, props.taskData, props.steps])

    const handleNavigationChange = useCallback(
        (value: string) => {
            const selectedOption = options.find(opt => opt.value === value)
            if (!selectedOption) return
            const jobId = currentJobId || paramJobId
            const targetPath = isRobotics
                ? `/preview/${paramJobId}?${new URLSearchParams({
                      childJobId: jobId as string,
                  }).toString()}`
                : `/preview/${paramJobId}`
            if (selectedOption.isDefault && selectedOption.stepId) {
                navigate(targetPath, {
                    state: {
                        jobId,
                        stepId: selectedOption.stepId,
                        taskId: undefined,
                    },
                })
            } else if (selectedOption.taskId) {
                navigate(targetPath, {
                    state: {
                        jobId,
                        stepId: undefined,
                        taskId: selectedOption.taskId,
                    },
                })
            }
        },
        [options, navigate, paramJobId, currentJobId, isRobotics]
    )

    const handleChildJobChange = useCallback(
        (value: string) => {
            if (isRobotics) {
                setSearchParams(
                    prevSearchParams => {
                        const nextSearchParams = new URLSearchParams(prevSearchParams)
                        nextSearchParams.set('childJobId', value)
                        return nextSearchParams
                    },
                    { replace: true }
                )
                return
            }
            navigate(`/preview/${paramJobId}`, {
                state: {
                    jobId: value,
                    stepId: undefined,
                    taskId: undefined,
                },
            })
        },
        [navigate, paramJobId, isRobotics, setSearchParams]
    )

    const childJobOptions = useMemo(() => {
        return props.childJobs.map(childJob => ({
            key: childJob.id,
            label: childJob.id,
            value: childJob.id,
        }))
    }, [props.childJobs])

    const cardContent = useMemo(
        () => (
            <div className={classes.card}>
                <div className={classes.parentJobSection}>
                    <div className={classes.purpleAccent} />
                    <div className={classes.parentJobContent}>
                        <div className={classes.parentJobLabel}>Parent job ID:</div>
                        <div className={classes.parentJobId}>{paramJobId}</div>
                    </div>
                </div>
                <div className={classes.dropdownsContainer}>
                    {childJobOptions.length > 0 && (
                        <div className={classes.dropdownSection}>
                            <div className={classes.dropdownLabel}>Select child job ID</div>
                            <Select
                                className={classes.dropdown}
                                options={childJobOptions}
                                value={currentJobId}
                                style={{ width: '100%' }}
                                dropdownStyle={DROPDOWN_STYLE}
                                popupMatchSelectWidth={false}
                                onChange={handleChildJobChange}
                            />
                        </div>
                    )}
                    <div className={classes.dropdownSection}>
                        <div className={classes.dropdownLabel}>Select step</div>
                        <Select
                            className={classes.dropdown}
                            options={options}
                            optionRender={getOptionRenderComponent}
                            value={currentValue}
                            style={{ width: '100%' }}
                            dropdownStyle={DROPDOWN_STYLE}
                            popupMatchSelectWidth={false}
                            onChange={handleNavigationChange}
                        />
                    </div>
                </div>
            </div>
        ),
        [
            paramJobId,
            childJobOptions,
            currentJobId,
            handleChildJobChange,
            options,
            getOptionRenderComponent,
            currentValue,
            handleNavigationChange,
        ]
    )

    return (
        <Popover
            content={cardContent}
            trigger="click"
            open={cardDialog.isOpen}
            onOpenChange={cardDialog.toggle}
            placement="bottomLeft"
            rootClassName={classes.popoverRoot}
        >
            <Button type="default" className={classes.changeViewButton} icon={<EyeFilled />}>
                Change view
            </Button>
        </Popover>
    )
}
